import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react'
import { validate_email_address } from '../libs/validate';
import Button from './Button';
import axios from 'axios';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import ReactLoading from 'react-loading';
import { mixpanel_client_track } from '../libs/mixpanelClient';

const Form = ({
  user_data,
  set_user_data
}: {
  user_data: any
  set_user_data: any
}) => {
  // States
  const [first_name, set_first_name] = useState(user_data?.first_name || "");
  const [last_name, set_last_name] = useState(user_data?.last_name || "");
  const [email_address, set_email_address] = useState(user_data?.email_address || "");
  const [organization_name, set_organization_name] = useState(user_data?.organization_name || "");
  const [position, set_position] = useState(user_data?.position || "");

  // Status messages
  const [error_message, set_error_message] = useState("")
  const [success_message, set_success_message] = useState("")

  // Status
  const [is_awaiting, set_is_awaiting] = useState(false)
  const [is_submitted, set_is_submitted] = useState(false)

  // Sync local state -> external user_data
  useEffect(() => {
    set_user_data({
      ...user_data,
      first_name,
      last_name,
      email_address,
      organization_name,
      position,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first_name, last_name, email_address, organization_name, position])

  const handle_user_input = (type: string, value: string) => {
    switch (type) {
      case 'first_name':
        set_first_name(value)
        break
      case 'last_name':
        set_last_name(value)
        break
      case 'email_address':
        set_email_address(value)
        break
      case 'organization_name':
        set_organization_name(value)
        break
      case 'position':
        set_position(value)
        break
      default:
        break
    }
    set_error_message("")
  }

  const submit = async () => {
    set_error_message("")
    set_is_awaiting(true)

    if (!first_name) {
      set_is_awaiting(false)
      set_error_message("Please enter your first name")
      return
    }
    if (!last_name) {
      set_is_awaiting(false)
      set_error_message("Please enter your last name")
      return
    }
    if (!email_address) {
      set_is_awaiting(false)
      set_error_message("Please enter your email address")
      return
    }
    if (!validate_email_address(email_address)) {
      set_is_awaiting(false)
      set_error_message("Please enter a valid email address")
      return
    }
    if (!organization_name) {
      set_is_awaiting(false)
      set_error_message("Please enter your organization")
      return
    }
    if (!position) {
      set_is_awaiting(false)
      set_error_message("Please enter your title")
      return
    }

    const post_signup_res = await axios.post(`https://app.vitalstrata.com/api/demo/signup`, {
      first_name,
      last_name,
      email_address,
      organization_name,
      position,
    })

    if (!post_signup_res.data.success) {
      set_is_awaiting(false)
      return
    }

    switch (post_signup_res.data.status) {
      case 'SUCCESS':
        set_is_awaiting(false)
        set_is_submitted(true)
        set_success_message('Our team member will reach out to you within 12 hours')

        // Mixpanel tracking
        mixpanel_client_track("www_demo_form_submitted", null)

        break
      default:
        set_is_awaiting(false)
        break
    }
  }

  // Shared styles (dark theme)
  const labelCls = 'text-[11px] tracking-widest uppercase text-white/60 font-light';
  const inputCls = clsx(
    'mt-2 block w-full h-12 bg-transparent text-white font-light',
    'border border-white/20 focus:border-white/40 outline-none',
    'px-3 rounded-none placeholder-white/40'
  );

  return (
    <div id="form" className="w-full h-full">
      {/* Mobile and Desktop share the same layout style */}
      <div className="flex flex-col py-2 sm:py-4 space-y-5">
        {/* Name row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className={labelCls}>First name</div>
            <input
              type="text"
              autoComplete="given-name"
              className={inputCls}
              value={first_name}
              onChange={(e) => handle_user_input('first_name', e.target.value)}
            />
          </div>
          <div>
            <div className={labelCls}>Last name</div>
            <input
              type="text"
              autoComplete="family-name"
              className={inputCls}
              value={last_name}
              onChange={(e) => handle_user_input('last_name', e.target.value)}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <div className={labelCls}>Business email</div>
          <input
            type="email"
            autoComplete="email"
            className={inputCls}
            value={email_address}
            onChange={(e) => handle_user_input('email_address', e.target.value)}
          />
        </div>

        {/* Organization */}
        <div>
          <div className={labelCls}>Organization name</div>
          <input
            type="text"
            className={inputCls}
            value={organization_name}
            onChange={(e) => handle_user_input('organization_name', e.target.value)}
          />
        </div>

        {/* Job title */}
        <div>
          <div className={labelCls}>Job title</div>
          <input
            type="text"
            className={inputCls}
            value={position}
            onChange={(e) => handle_user_input('position', e.target.value)}
          />
        </div>

        {/* Error/Success */}
        {error_message ? (
          <div className="flex space-x-2 items-start">
            <ExclamationTriangleIcon className="pt-[2px] h-4 w-4 min-w-4 min-h-4 text-red-400" />
            <div className="text-sm font-light text-red-400">{error_message}</div>
          </div>
        ) : null}
        {success_message ? (
          <div className="flex space-x-2 items-start">
            <CheckCircleIcon className="pt-[2px] h-4 w-4 min-w-4 min-h-4 text-green-300" />
            <div className="text-sm font-light text-green-300">{success_message}</div>
          </div>
        ) : null}

        {/* Submit */}
        <div>
          <Button
            onClick={is_submitted ? null : submit}
            variant="secondary"
            className={clsx('rounded-none px-6', is_submitted ? 'cursor-default' : '')}
            disabled={is_submitted}
          >
            {is_awaiting ? (
              <ReactLoading type="spokes" color="#000000" height={20} width={20} />
            ) : (
              <span className="font-normal">Submit</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Form